<script lang="ts">
  import * as Dialog from "$lib/components/ui/dialog";
  import { authStore } from "$lib/stores/authStore";
  import { TableAPI } from "$lib/api/table";
  import { goto } from "$app/navigation";
  import { tableHistoryStore } from "$lib/stores/tableHistoryStore";
  import { tableStore } from "$lib/stores/table-store";
  import { ArrowLeft, Box, Library, Table } from "lucide-svelte";
  import { Button } from "$lib/components/ui";
  import { cn } from "$lib/utility";
  import { TemplateAPI, type Template } from "$lib/api/template";
  import { Loader2 } from "lucide-svelte";
  import TemplateCard from "../../../routes/(app)/templates/_components/TemplateCard.svelte";
  import { ScrollArea } from "$lib/components/ui/scroll-area";
  import { toast } from "svelte-sonner";
  import ShortcutKey from "./ShortcutKey.svelte";
  import { onMount } from "svelte";

  let selectingFromTemplate = $state(false);
  let isLoading = $state(false);
  let prioritizedTemplates: Template[] = $state([]);
  let personalTemplates: Template[] = $state([]);
  let teamTemplates: Template[] = $state([]);
  let publicTemplates: Template[] = $state([]);
  let selectedTemplateId: string | null = $state(null);

  const { setShowCreateTableDialog } = tableStore;

  const user = authStore.getUser();
  const userId = user?.id || 0;

  function handleKeyPress(event: KeyboardEvent) {
    if (!$tableStore.showCreateTableDialog) return;

    if (event.key.toLowerCase() === "b") {
      createBlankTable();
    } else if (event.key.toLowerCase() === "t") {
      selectingFromTemplate = true;
    }
  }

  onMount(() => {
    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  });

  async function loadPrioritizedTemplates() {
    const prioritizedTemplatesResponse = await TemplateAPI.getTemplates({
      showInCreateDialog: true,
    });

    prioritizedTemplates = prioritizedTemplatesResponse;
  }

  async function loadTemplates() {
    isLoading = true;
    try {
      const allTemplates = await TemplateAPI.getTemplates({});

      personalTemplates = allTemplates.filter(
        (template) => template.createdBy === userId && template.visibility === "private",
      );
      teamTemplates = allTemplates.filter((template) => template.visibility === "team");
      publicTemplates = allTemplates.filter((template) => template.visibility === "public");
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    } finally {
      isLoading = false;
    }
  }

  $effect(() => {
    if (selectingFromTemplate) {
      loadTemplates();
    }
  });

  $effect(() => {
    if (!selectingFromTemplate) {
      loadPrioritizedTemplates();
    }
  });

  async function createBlankTable() {
    if (!userId) return;
    try {
      const data = await TableAPI.createTable({
        name: "New sheet",
        numColumns: 10,
        numRows: 10,
      });

      const now = new Date();
      const newTable = {
        id: data.id,
        publicId: data.id,
        name: "New sheet",
        visibility: "private" as const,
        createdAt: now,
        updatedAt: now,
        ownerId: userId,
      };

      logger.log("Adding new table to store with current date:", newTable);

      tableHistoryStore.appendTables([newTable]);
      setShowCreateTableDialog(false);
      goto(`/table/${data.id}`);
    } catch (error) {
      toast.error("Error creating table");
      console.error("Error creating table", error);
    }
  }

  async function createTableFromTemplate() {
    if (!selectedTemplateId) return;
    if (!userId) return;

    try {
      const data = await TableAPI.createTable({
        name: "New sheet",
        numColumns: 10,
        numRows: 10,
        templateId: selectedTemplateId,
      });

      const now = new Date();
      const newTable = {
        id: data.id,
        publicId: data.id,
        name: "New sheet",
        visibility: "private" as const,
        createdAt: now,
        updatedAt: now,
        ownerId: userId,
      };

      logger.log("Adding new table to store with current date:", newTable);

      tableHistoryStore.appendTables([newTable]);
      setShowCreateTableDialog(false);
      goto(`/table/${data.id}`);
    } catch (error) {
      toast.error("Error creating table");
      console.error("Error creating table", error);
    }
  }
</script>

<Dialog.Root
  open={$tableStore.showCreateTableDialog}
  onOpenChange={(value) => {
    setShowCreateTableDialog(value);
    selectingFromTemplate = false;
  }}
>
  <Dialog.Content
    id="create-table-dialog"
    class={cn(
      "w-full gap-1 border-sidebar-border transition-none dark:border-white/20",
      selectingFromTemplate ? "h-[90vh] sm:max-w-5xl" : "sm:max-w-2xl",
    )}
  >
    <Dialog.Header>
      <Dialog.Title class="text-lg font-medium">
        {#if !selectingFromTemplate}
          Create Table
        {:else}
          <div class="flex items-center gap-2">
            <Button
              onclick={() => (selectingFromTemplate = false)}
              variant="outline"
              class={cn(
                "flex h-7 items-center justify-center gap-1.5 !rounded-full text-2xs font-normal text-theme-text-primary dark:border-white dark:border-white/20 dark:bg-theme-background-tertiary dark:hover:border-theme-text-secondary dark:hover:text-theme-text-primary",
                "size-7",
              )}
            >
              <ArrowLeft size={16} />
            </Button>
            <span>Select a template</span>
          </div>
        {/if}
      </Dialog.Title>
      <Dialog.Description>
        {#if !selectingFromTemplate}
          Start with a blank table or use a template.
        {:else}
          <span class="ml-11">Pick a template to get started.</span>
        {/if}
      </Dialog.Description>
    </Dialog.Header>
    {#if !selectingFromTemplate}
      <div class="mt-10 flex items-center gap-3">
        <button
          type="button"
          id="create-blank-table-button"
          onclick={() => createBlankTable()}
          class="flex relative min-h-28 w-1/2 flex-col items-center justify-center gap-1 border border-sidebar-border py-5 hover:border-black dark:border-white dark:border-white/20"
        >
          <ShortcutKey key="B" className="absolute top-2 right-2" />
          <Table size={24} class="mb-4 size-6 stroke-1 text-theme-text-secondary" />
          <span class="text-sm font-medium">Blank</span>
          <p class="text-sm text-theme-text-secondary">Start with a blank table.</p>
        </button>

        <button
          type="button"
          onclick={() => (selectingFromTemplate = true)}
          class="flex min-h-28 relative w-1/2 flex-col items-center justify-center gap-1 border border-sidebar-border py-5 hover:border-black dark:border-white dark:border-white/20"
        >
          <ShortcutKey key="T" className="absolute top-2 right-2" />

          <Library size={24} class="mb-4 size-6 stroke-1 text-theme-text-secondary" />
          <span class="text-sm font-medium">Template</span>
          <p class="text-sm text-theme-text-secondary">Start with a template.</p>
        </button>
      </div>
      <div class="relative mt-5 border-t border-sidebar-border pt-4">
        <p class="absolute -top-3 left-1/2 -translate-x-1/2 bg-background px-5 text-xs text-theme-text-secondary">
          Or use these recommended templates
        </p>
        <div class="grid grid-cols-2 gap-3">
          {#each prioritizedTemplates as template}
            <button
              type="button"
              class="w-full"
              onclick={() => {
                selectedTemplateId = template.id;
                createTableFromTemplate();
              }}
            >
              <TemplateCard
                id={template.id}
                name={template.title}
                description={template.description}
                categories={template.categories}
                isOfficial={template.isOfficial}
              />
            </button>
          {/each}
        </div>
      </div>
    {:else}
      <ScrollArea class="mt-5 flex h-[69vh] flex-col">
        {#if isLoading}
          <div class="flex justify-center py-8">
            <Loader2 class="size-8 animate-spin text-theme-text-secondary" />
          </div>
        {:else}
          <div class="flex flex-col gap-6">
            {#each [{ title: "My Private Templates", templates: personalTemplates }, { title: "Team Templates", templates: teamTemplates }, { title: "Public Templates", templates: publicTemplates }] as section}
              {#if section.templates.length > 0}
                <section>
                  <h3 class="mb-3 text-xs font-light uppercase text-theme-text-secondary">{section.title}</h3>
                  <div class="grid grid-cols-3 gap-3">
                    {#each section.templates as template}
                      <button
                        type="button"
                        class="w-full"
                        onclick={() =>
                          selectedTemplateId === template.id
                            ? (selectedTemplateId = null)
                            : (selectedTemplateId = template.id)}
                      >
                        <TemplateCard
                          id={template.id}
                          name={template.title}
                          description={template.description}
                          categories={template.categories}
                          isOfficial={template.isOfficial}
                          className={cn(
                            "text-left",
                            selectedTemplateId === template.id
                              ? "border-theme-dark26 dark:border-white dark:hover:border-white"
                              : "",
                          )}
                        />
                      </button>
                    {/each}
                  </div>
                </section>
              {/if}
            {/each}

            {#if !personalTemplates.length && !teamTemplates.length && !publicTemplates.length}
              <div class="py-8 text-center text-theme-text-secondary">No templates found</div>
            {/if}
          </div>
        {/if}
      </ScrollArea>

      <Button
        disabled={!selectedTemplateId}
        class={cn(
          "justift-center flex items-center gap-1.5 !rounded-base bg-black px-1.5 py-1 font-normal text-white dark:border-white dark:border-white/20 dark:bg-theme-green dark:text-black dark:hover:text-black",
        )}
        aria-label="Create table"
        onclick={() => createTableFromTemplate()}
      >
        Create Table
      </Button>
    {/if}
  </Dialog.Content>
</Dialog.Root>
